import { estypes } from "@elastic/elasticsearch";
import type {
  FieldConfiguration,
  SearchDriverOptions,
  SearchFieldConfiguration,
  SearchQuery
} from "@elastic/search-ui";
import ElasticsearchAPIConnector from "@elastic/search-ui-elasticsearch-connector";
import NestedFieldSearchService from "context/elasticsearch/nestedFields/nestedFieldSearchService";
import { CodeSystem, ElasticsearchConfig } from "context/elasticsearch/types";

//import { request } from 'http'
//import { ESProperty } from 'utils/constants/elasticsearchConfig'
import { autocompleteQuery } from "./autocompleteQuery";
import { formatNestedFieldQuery } from "./queryUtils";
import { routingOptions } from "./routingOptions";

const searchKey = import.meta.env.VITE_ELASTICSEARCH_INDEX_API_KEY;
const elasticCloudID = import.meta.env.VITE_ELASTIC_CLOUD_ID;

const getSearchDriverOptions = (
  config: ElasticsearchConfig
): SearchDriverOptions => {
  if (!config || !searchKey || !elasticCloudID) {
    if (!searchKey) {
      throw new Error("Missing search key for Elasticsearch");
    }
    if (!elasticCloudID) {
      throw new Error("Missing Elastic Cloud ID for Elasticsearch");
    }
    throw new Error(`Configuration not found`);
  }

  const searchFields: Record<string, SearchFieldConfiguration> = {};
  for (const prop of config.properties) {
    if (prop.searchEnabled) {
      searchFields[prop.propertyName] = {
        weight: prop.weight
      };
    }
  }

  const resultFields: Record<string, FieldConfiguration> = {};
  for (const prop of config.properties) {
    if (prop.returnInResults) {
      resultFields[prop.propertyName] = {
        snippet: { size: 200, fallback: true },
        raw: {}
      };
    }
  }

  const connector = new ElasticsearchAPIConnector({
    cloud: {
      id: elasticCloudID
    },
    index: config.indexName,
    apiKey: searchKey,
    interceptSearchRequest: async (
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      { requestBody, requestState, queryConfig },
      next
    ) => {
      // If search term is present, apply minimum score
      if (requestState.searchTerm && requestState.searchTerm.trim() !== "") {
        requestBody.min_score = config.minimumScore;
      }

      // Modify query types (not for KIS)
      if (
        config.codeSystem != CodeSystem.Adm &&
        requestBody.query?.bool?.should &&
        Array.isArray(requestBody.query.bool.should)
      ) {
        requestBody.query.bool.should = requestBody.query.bool.should.filter(
          (query: estypes.QueryDslQueryContainer) =>
            query.multi_match?.type == "cross_fields" ||
            query.multi_match?.type == "phrase" ||
            query.multi_match?.type == "phrase_prefix" ||
            query.multi_match?.type == "best_fields"
        );

        for (const query of requestBody.query.bool.should) {
          if (query.multi_match == undefined) continue;

          if (
            //query.multi_match?.type == 'cross_fields' ||
            query.multi_match?.type == "phrase" ||
            query.multi_match?.type == "phrase_prefix" ||
            query.multi_match?.type == "best_fields"
          ) {
            query.multi_match.minimum_should_match = "2<3 3<60% 7<70% 10<80%";
          }
        }
      }

      // Middleware to modify sort property if it is score (not for KIS)
      if (
        config.codeSystem != CodeSystem.Adm &&
        requestBody.sort &&
        Array.isArray(requestBody.sort) &&
        requestBody.sort.length > 0 &&
        Object.keys(requestBody.sort[0])[0] === "_score"
      ) {
        // If sorting by score, we want to sort by _score desc
        requestBody.sort = [
          { _score: { order: "desc" } },
          { "code.enum": { order: "asc", missing: "_last" } }
        ];
      }

      // Middleware to modify sort property if it is not score
      // Should use enum subfield for sorting
      if (
        requestBody.sort &&
        Array.isArray(requestBody.sort) &&
        requestBody.sort.length > 0 &&
        Object.keys(requestBody.sort[0])[0] !== "_score"
      ) {
        // key is the field to sort by, value is the direction
        // Just add .enum to the field name
        requestBody.sort = requestBody.sort.map((sortItem) => {
          if (typeof sortItem === "string") {
            return `${sortItem}.enum`;
          } else if (typeof sortItem === "object") {
            const key = Object.keys(sortItem)[0];
            return { [`${key}.enum`]: sortItem[key] };
          }
          return sortItem;
        });
      }
      // Middleware to add nested field queries to the request body
      // Not natively supported by Search UI, so we add it manually

      // Remove existing nested queries to avoid duplicates
      if (
        requestBody.query?.bool?.should &&
        Array.isArray(requestBody.query.bool.should)
      ) {
        requestBody.query.bool.should = requestBody.query.bool.should.filter(
          (query: estypes.QueryDslQueryContainer) => !query.nested
        );
      }

      // Get current nested field search state from service
      const nestedFieldService = NestedFieldSearchService.getInstance();
      const shouldSearchNestedFields = nestedFieldService.getSetting(
        config.codeSystem,
        false
      );

      //console.log(shouldSearchNestedFields, config.codeSystem)

      const searchAbleFields = config.nestedProperties?.some((nestedProperty) =>
        nestedProperty.nestedFields.some((field) => field.searchEnabled)
      );

      if (
        !config.nestedProperties ||
        config.nestedProperties.length === 0 ||
        !searchAbleFields ||
        !shouldSearchNestedFields
      ) {
        // console.log('Not adding any nested queries.')
        // console.log('Exists nested properties?', !config.nestedProperties)
        // console.log(
        // 	'Zero nested properties?',
        // 	config.nestedProperties?.length === 0
        // )
        // console.log('Exists nested fields?', !searchAbleFields)
        // console.log(
        // 	'Should not search nested fields?',
        // 	!shouldSearchNestedFields
        // )
        // If there are no nested properties, return the request body as is
        return await next(requestBody);
      }

      //console.log('Adding nested queries...')
      // Bypass undefined search term
      const searchTerm = "" + requestState.searchTerm;

      const nestedFields = config.nestedProperties
        .filter((nestedProp) => nestedProp.searchEnabled)
        .flatMap((nestedField) =>
          formatNestedFieldQuery(
            nestedField,
            searchTerm,
            config.maximumNestedFieldValues
          )
        );

      // Add nested queries if there exists a query in the first place
      if (
        requestBody.query?.bool?.should &&
        Array.isArray(requestBody.query.bool.should) &&
        nestedFields.length > 0
      ) {
        // Add the nested queries to the should clause
        requestBody.query.bool.should.push(...nestedFields);
      }
      //return requestBody

      // Take best of nested and normal field hits (dont add synonym hits to code total score)
      // Not for KIS
      const qb = requestBody?.query?.bool;
      if (
        config.codeSystem !== CodeSystem.Adm &&
        qb?.should &&
        Array.isArray(qb.should) &&
        qb.should.length > 0
      ) {
        const multiMatchClauses = qb.should.filter(
          (q: estypes.QueryDslQueryContainer) => q?.multi_match
        );
        const nestedClauses = qb.should.filter(
          (q: estypes.QueryDslQueryContainer) => q?.nested
        );
        const otherClauses = qb.should.filter(
          (q: estypes.QueryDslQueryContainer) => !q?.multi_match && !q?.nested
        );

        // Only transform if we actually have at least one multi_match OR nested clause.
        if (multiMatchClauses.length > 0 || nestedClauses.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const disMaxQueries: any[] = [];

          if (multiMatchClauses.length > 0) {
            disMaxQueries.push({
              bool: {
                should: multiMatchClauses,
                minimum_should_match: 1
              }
            });
          }

          if (nestedClauses.length > 0) {
            // Combine nested queries under a single bool so they compete as a group
            disMaxQueries.push({
              bool: {
                should: nestedClauses,
                minimum_should_match: 1
              }
            });
          }

          if (otherClauses.length > 0) {
            // Preserve any non-multi_match/non-nested clauses as their own competitor
            disMaxQueries.push({
              bool: {
                should: otherClauses,
                minimum_should_match: 1
              }
            });
          }

          // Replace the whole query with dis_max so the best branch wins.
          // tie_breaker: 0 => pure max; set small >0 (e.g. 0.1) if you want secondary branches to contribute slightly.
          requestBody.query = {
            dis_max: {
              tie_breaker: 0,
              queries: disMaxQueries
            }
          };
        }
      }

      return await next(requestBody);
    }
  });

  // Add '.enum' to facet field names
  const disjunctiveFacetFields =
    config.facetFields?.map((f) => f + ".enum") || [];

  const facetFieldsObject: Record<string, { type: string }> = {};
  for (const facetField of disjunctiveFacetFields) {
    facetFieldsObject[facetField] = { type: "value" };
  }

  const searchQuery: SearchQuery = {
    disjunctiveFacets: disjunctiveFacetFields,
    facets: facetFieldsObject,
    result_fields: resultFields,
    search_fields: searchFields
  };

  const options: SearchDriverOptions = {
    apiConnector: connector,
    alwaysSearchOnInitialLoad: true,
    hasA11yNotifications: false,
    autocompleteQuery: autocompleteQuery,
    routingOptions: routingOptions,
    searchQuery: searchQuery
  };

  return options;
};

export default getSearchDriverOptions;
